<template>
    <div
        class="unit-coin"
        @click="onSelectUnit"
        :class="{
            selected: unitIsSelected,
            [toClassName(unit.state)]: unit.state,
            [toClassName(unit.type)]: unit.state,
            selectable: isSelectable,
            removable: removable,
            resupplyable: resupplyable,
			[classes]: classes
        }"
    >
        <unit-image :unitType="unit.type" :unitState="unit.state" :theme="gameState.theme"/>
        <unit-image :unitType="facedownUnit.type" classes="facedown-unit-image" v-if="facedownUnit" />
        <div class="actions-wrapper" v-if="showActions">
            <span
                class="btn-icon recruit bottom-right"
                v-if="recruitable"
                @click="onCommand('recruit')"
            >
                <fa-icon icon="coins" />
            </span>

            <span
                class="btn-icon flipped bottom-left"
                v-if="canPass"
                @click="onCommand('pass')"
            >
                <fa-icon icon="reply" />
            </span>
            <span
                class="btn-icon cure-poison-btn top-right-corner"
                @click="onCommand('cure_poison')"
                v-if="canCurePoison"
            >
                    <icon class="action-icon cure-poison-icon" icon="cure_poison" />
            </span>
            <span
                class="btn-icon return-decoy-coin-btn top-right-corner"
                @click="onCommand('decoy_return')"
                v-if="canReturnDecoyCoin"
            >
				<ArrowTurnUp class="action-icon return-decoy-coin-icon arrow-up-icon" />
            </span>
            <span
                class="btn-icon bottom-right"
                v-if="canClaimInitiative"
                @click="onCommand('claim_initiative')"
            >
                <fa-icon icon="crown" />
            </span>
			<span
                class="btn-icon bottom-right"
                v-if="canGiveInitiative"
                @click="onCommand('give_initiative')"
            >
                <fa-icon icon="crown" />
				<span class="action-icon slash-wrapper">
					<span class="action-icon-slash" />
					<span class="action-icon-slash" />
				</span>
            </span>
            <span
                class="btn-icon bolster top-right"
                v-if="bolsterable"
                @click="onCommand('bolster')"
            >
                <fa-icon class="action-icon shield-icon" icon="shield" />
                <fa-icon class="action-icon plus-icon" icon="plus" />
            </span>
            <span
                class="btn-icon unbolster top-left"
                v-if="unbolsterable"
                @click="onCommand('unbolster')"
            >
                <fa-icon class="action-icon shield-icon" icon="shield" />
                <fa-icon class="action-icon minus-icon" icon="minus" />
            </span>
            <span class="btn-group center combat-actions-group">
                <span
                    class="btn-icon attack"
                    v-if="attackable"
                    @click="onCommand('attack')"
                >
                    <icon class="action-icon" icon="sword" />
                </span>
                <span
                    class="btn-icon poison"
                    v-if="poisonable"
                    @click="onCommand('poison')"
                >
                    <icon class="action-icon" icon="poison" />
                </span>
                <span
                    class="btn-icon shock"
                    v-if="shockable"
                    @click="onCommand('shock')"
                >
					<ShockIcon class="action-icon" />
                </span>
				<span
                    class="btn-icon maneuver"
                    v-if="canBeManeuvered"
                    @click="onCommand('maneuver')"
                >
                    <fa-icon class="action-icon" icon="person-running" />
                </span>
				<span
                    class="btn-icon shocked-maneuver"
                    v-if="canShockedManeuver"
                    @click="onCommand('shocked_maneuver')"
                >
                    <fa-icon class="action-icon" icon="person-running" />
					<ShockIcon class="action-icon shock-icon" />
                </span>
            </span>
            <span
                class="btn-icon attack center sacrifice-attack"
                v-if="sacrificeAttackable"
                @click="onCommand('sacrifice_attack')"
            >
                <icon class="action-icon" icon="sword" />
                <fa-icon class="action-icon drop" icon="tint" />
            </span>
            <span
                class="btn-icon center remove"
                v-if="removable"
                @click="onCommand('remove')"
            >
                <fa-icon icon="times" />
            </span>
            <span
                class="btn-icon bottom-right resupply"
                v-if="resupplyable"
                @click="onCommand('resupply')"
            >
                <fa-icon icon="heart" />
            </span>
            <span
                class="btn-icon center swap"
                v-if="swappable"
                @click="onCommand('swap')"
            >
                <fa-icon icon="right-left" />
            </span>
            <span
                class="btn-icon center mimic"
                v-if="mimicable"
                @click="onCommand('mimic')"
            >
                <fa-icon icon="book" />
            </span>
        </div>
        <img class="poison-token" v-if="unit.poisonToken" :src="getAssetUrl(`@/assets/${poisonTokenImage}.png`)" />
    </div>
</template>

<script>
import find from 'lodash.find';
import { getAssetUrl } from '../../helpers/assets';
import { API_BASE_URL } from '@/helpers/env';
import {
    DECREE_TYPE_ENLIST,
    DECREE_TYPE_REDEPLOY,
    DECREE_TYPE_REINFORCE,
} from '../DecreeCard/DecreeCard-types';
import {
    UNIT_STATE_DEPLOYED,
    UNIT_STATE_DISCARDED_FACEDOWN,
    UNIT_STATE_IN_HAND,
    UNIT_TYPE_BISHOP,
} from '../Unit/Unit-types';
import ArrowTurnUp from '../../assets/arrow_turn_up.svg';
import ShockIcon from '../../assets/shock_icon.svg';

export default {
	components: {
		ArrowTurnUp,
		ShockIcon,
	},
    name: 'UnitCoin',
    props: [
		'classes',
        'index',
		'multipleTargets',
        'onCommandCallback',
        'showActions',
        'unit',
    ],
    computed: {
        actions() {
            return this.actionsState.actions;
        },
        actionsState() {
            return this.$store.state.actionsState;
        },
        activeDecree() {
            return this.actionsState.activeDecree;
        },
        apiUrl() {
            return API_BASE_URL;
        },
        attackable() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'attack' &&
                action.opponentUnitId === this.unit.id &&
                action.friendlyUnitId === this.friendlyUnitId,
            ).length;
        },
        poisonable() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'poison' &&
                action.opponentUnitId === this.unit.id &&
                action.friendlyUnitId === this.friendlyUnitId,
            ).length;
        },
        shockable() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'shock' &&
                action.opponentUnitId === this.unit.id &&
                action.friendlyUnitId === this.friendlyUnitId,
            ).length;
        },
        canShockedManeuver() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'shocked_maneuver' &&
                action.friendlyUnitId === this.unit.id,
            ).length;
        },
		canBeManeuvered() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'maneuver' &&
                action.friendlyUnitId === this.unit.id,
            ).length;
        },
        bolsterable() {
            let bolsterUnitIds = [];
            const bolsterActions = this.selectedUnitActions.filter(action => action.type === 'bolster');

            bolsterActions.map((action) => {
                bolsterUnitIds = bolsterUnitIds.concat(action.friendlyUnitIds);
            });

            return bolsterUnitIds.includes(this.unit.id);
        },
        canClaimInitiative() {
            return find(this.selectedUnitActions, { unitId: this.unit.id, type: 'claim_initiative' });
        },
		canGiveInitiative() {
			const giveInitiativeAction = find(this.selectedUnitActions, { unitId: this.unit.id, type: 'give_initiative' });
            return giveInitiativeAction && !giveInitiativeAction.options?.length;
        },
        canPass() {
            return find(this.selectedUnitActions, { unitId: this.unit.id, type: 'pass' });
        },
        canCurePoison() {
            return find(this.selectedUnitActions, { unitId: this.unit.id, type: 'cure_poison' });
        },
        canReturnDecoyCoin() {
            return find(this.selectedUnitActions, { unitId: this.unit.id, type: 'decoy_return' });
        },
        disabled() {
            return this.unit.playerId === this.player.id &&
                this.unit.state === UNIT_STATE_IN_HAND &&
                !find(this.actions, { unitId: this.unit.id }) &&
                !this.isSpectator;
        },
        facedownUnit() {
            return this.unit.state === UNIT_STATE_DISCARDED_FACEDOWN &&
                (this.facedownUnits[this.index] || this.isReplay);
        },
        facedownUnits() {
            return this.gameState.facedownUnits;
        },
        friendlyUnitId() {
            return this.actionsState.friendlyUnitId;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        gameState() {
            return this.$store.state.gameState;
        },
        isGame() {
            return this.$route.name === 'game';
        },
        isSelectable() {
            return (this.actionsState.selectableUnitIds.includes(this.unit.id) ||
                this.actionsState.selectableUnitIds.includes(this.unit.parentUnitId)) &&
                !this.isSpectator;
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        mimicable() {
            return this.selectedUnitActions.find(action =>
                action.type === 'mimic' &&
                action.friendlyUnitId === this.unit.id,
            );
        },
        player() {
            return this.isGame ? this.$store.getters.player : this.$store.getters.replayPlayer;
        },
        players() {
            return this.gameState.players;
        },
        recruitable() {
            const recruitAction = find(this.selectedUnitActions, { type: 'recruit' });
            return recruitAction && recruitAction.friendlyUnitIds.includes(this.unit.id);
        },
        replayState() {
            return this.$store.state.replayState;
        },
        removable() {
            return this.actionsState.removableUnitIds.includes(this.unit.id) &&
                !this.isSpectator;
        },
        resupplyable() {
            return this.actionsState.resupplyUnitIds.includes(this.unit.id) &&
                !this.isSpectator;
        },
        sacrificeAttackable() {
            return this.selectedUnitActions.filter(action =>
                action.type === 'sacrifice_attack' &&
                action.opponentUnitId === this.unit.id &&
                action.friendlyUnitId === this.friendlyUnitId,
            ).length;
        },
        selectedUnitActions() {
            return this.actionsState.selectedUnitActions;
        },
        submittingAction() {
            return this.actionsState.submitting;
        },
        swappable() {
            return find(this.selectedUnitActions, {
                type: 'swap',
                friendlyUnitId: this.unit.id,
            }) && !this.isSpectator;
        },
        unbolsterable() {
            let unbolsterUnitIds = [];
            const unbolsterActions = this.selectedUnitActions.filter(action => action.type === 'unbolster');

            unbolsterActions.map((action) => {
                unbolsterUnitIds = unbolsterUnitIds.concat(action.friendlyUnitId);
            });

            return unbolsterUnitIds.includes(this.unit.id);
        },
        unitIsSelected() {
            const { selectedUnitId, friendlyUnitId } = this.actionsState;

            return (selectedUnitId && selectedUnitId === this.unit.id) ||
                (friendlyUnitId && friendlyUnitId === this.unit.id);
        },
        userId() {
            return this.$store.state.loginState.userId;
        },
        isReplay() {
            return this.$route.name === 'replay';
        },
        poisonTokenImage() {
            let imageName = 'poison_token';

            let gameState = this.gameState;

            if (this.isReplay) {
                gameState = this.replayState;
            }

            if (this.unit.poisonToken && gameState.unitTypes.length) {
                const unitType = find(gameState.unitTypes, {
                    id: this.unit.poisonToken.ownerUnitTypeId,
                });

                if (unitType) {
                    imageName = `${imageName}_${unitType.type.toLowerCase()}`;
                }
            }

            return imageName;
        },
    },
    methods: {
        getAssetUrl,
        getUnitType(unitType) {
            return find(this.gameState.unitTypes, { type: unitType });
        },
        async onCommand(actionType) {
            if (['attack', 'sacrifice_attack', 'poison', 'shock'].includes(actionType)) {

				if (actionType !== 'shock' && this.multipleTargets) {
					this.$store.dispatch('setShowTargetsModal', true);
					return;
				}

                const action = find(this.actionsState.selectedUnitActions, {
                    type: actionType,
                    unitId: this.actionsState.selectedUnitId,
                    opponentUnitId: this.unit.id,
                });

                let targetPosition = this.actionsState.selectedPosition;

                if (!targetPosition || !action.targetPositions.includes(targetPosition)) {
                    targetPosition = action.targetPositions[0];
                }

                this.$store.dispatch('setOpponentUnitId', this.unit.id);

                if (targetPosition) {
                    this.$store.dispatch('setSelectedPosition', targetPosition);
                }
            } else {
                this.$store.dispatch('setSelectedPosition', null);
            }

            if (['bolster', 'recruit', 'swap', 'mimic', 'maneuver', 'shocked_maneuver'].includes(actionType)) {
                this.$store.dispatch('setFriendlyUnitId', this.unit.id);
            }

            if (actionType === 'remove') {
				if (this.multipleTargets) {
					this.$store.dispatch('setShowTargetsModal', true);
					return;
				}

                this.$store.dispatch('setSelectedUnitId', this.unit.id);
                this.$store.dispatch('setFriendlyUnitId', this.unit.id);
            }

            if (actionType === 'resupply') {
                this.$store.dispatch('setSelectedUnitId', this.unit.id);
                this.$store.dispatch('setFriendlyUnitId', this.unit.id);
            }

            if (actionType === 'cure_poison') {
                const friendlyUnitIsPoisoned = !!this.gameState.poisonTokens.find(poisonToken =>
                    poisonToken.assignedUnitId === this.actionsState.friendlyUnitId,
                );

                const friendlyUnitId = (friendlyUnitIsPoisoned && this.actionsState.friendlyUnitId) ||
                    find(this.actionsState.selectedUnitActions, {
                        type: actionType,
                        unitId: this.actionsState.selectedUnitId,
                    }).friendlyUnitId;

                this.$store.dispatch('setFriendlyUnitId', friendlyUnitId);
            }

            await this.$store.dispatch('sendCommand',
                {
                    actionType,
                    gameId: this.gameId,
                    playerId: this.player.id,
                    $socket: this.$socket,
                    $notify: this.$notify,
                },
            );

            if (this.onCommandCallback && typeof this.onCommandCallback === 'function') {
                this.onCommandCallback();
            }
        },
        onSelectUnit() {
            this.$store.dispatch('setSoundReady');

            const { unit } = this;

            if (unit.state === UNIT_STATE_IN_HAND &&
                this.player.id !== this.gameState.activePlayerId) {
                this.$notify({
                    group: 'app',
                    clean: true,
                });
                this.$notify({
                    group: 'app',
                    title: '',
                    type: 'info',
                    text: 'Please wait for your turn',
                });
                return;
            }

            if (this.isSelectable && this.activeDecree === DECREE_TYPE_REDEPLOY) {
                return;
            }

            if (!this.isSelectable ||
                [
                    DECREE_TYPE_ENLIST,
                    DECREE_TYPE_REINFORCE,
                ].includes(this.activeDecree)) {
                return;
            }

            let friendlyUnitId = unit.state === UNIT_STATE_DEPLOYED ?
                unit.id :
                null;
            let unitId = unit.id;

            if (unit.parentUnitId) {
                unitId = unit.parentUnitId;
                friendlyUnitId = unit.parentUnitId;
            }

            this.$store.dispatch('selectUnit', { unitId, friendlyUnitId });

            if (unit.type === UNIT_TYPE_BISHOP &&
                unit.state === UNIT_STATE_IN_HAND) {
                if (this.$store.getters.deployedUnitTypes.includes(UNIT_TYPE_BISHOP)) {
                    const poisonedBishop = this.actionsState.selectedUnitActions.find(action => action.type === 'cure_poison');
                    this.$store.dispatch('setBishopRecruit', { value: !poisonedBishop, notify: false });
                } else {
                    this.$store.dispatch('setBishopRecruit', { value: false, notify: false });
                }
            }
        },
        toClassName(text) {
            return text ? text.toLowerCase().replace(/_/g, '-') : '';
        },
    },
};
</script>

<style scoped lang="scss">
 @use "@/styles/styles.scss" as *;

.unit-coin {
    border: 2.5px outset transparent;
    border-radius: 50%;
    box-sizing: border-box;
    display: inline-block;
    position: relative;
    user-select: none;

    &.deployed,
    &.in-hand {
        border: 2.5px outset transparent;
    }

    &.selectable {
        cursor: pointer;
    }

    .actions-wrapper {
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: 10;

        .btn-group {
            position: absolute;
            display: flex;
            width: 100%;
            justify-content: space-evenly;

            .btn-icon {
                position: relative;
            }

            &.center {
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
            }
        }

        .btn-icon {
            box-sizing: border-box;
            cursor: pointer;
            height: auto;
            max-height: 45%;
            padding: 10%;
            position: absolute;
            user-select: none;
            width: 45%;

			.return-decoy-coin-icon {
				max-width: 75%;
			}

            &.shocked-maneuver,
			&.maneuver,
            &.shock {
                padding: 8%;

                .action-icon {
                    height: auto;
                }

				.shock-icon {
					right: 13%;
					max-height: 20%;
					max-width: 20%;
					position: absolute;
					top: 18%;
				}
            }

            &.cure-poison-btn {
                background: $poison-cure;
            }

            &.bolster,
            &.unbolster {
                max-height: 44%;
                padding: 7%;
                width: 44%;

                .shield-icon {
                    width: 96%;
                }

                .plus-icon,
                .minus-icon {
                    border-radius: 50%;
                    color: $dark-blue;
                    left: 50%;
                    max-height: 40%;
                    max-width: 40%;
                    position: absolute;
                    top: 50%;
                    transform: translate(-50%, -50%);
                }
            }


            &.recruit {
                background: $burnished-gold;
                border: 1px solid $burnished-gold;
            }

            &.resupply {
                background: $lancer;
            }

            &.top-right-corner {
                right: 0;
                top: 0;
                transform: translate(70%, -70%);
            }

            &.bottom-left {
                bottom: 0;
                left: 0;
                transform: translate(-70%, 70%);

                &.flipped {
                    transform: translate(-70%, 70%) scaleX(-1);
                }
            }

            &.bottom-right {
                bottom: 0;
                right: 0;
                transform: translate(70%, 70%);
            }

            &.center {
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
            }

            &.top-left {
                left: 0;
                top: 0;
                transform: translate(-30%, -30%);
            }

            &.top-right {
                right: 0;
                top: 0;
                transform: translate(30%, -30%);
            }

            .action-icon {

                &.drop {
                    position: absolute;
                    width: 18%;
                    height: auto;
                    right: 14%;
                    bottom: 30%;
                }
            }
        }

        .btn-icon-group {
            cursor: pointer;
            height: auto;
            position: absolute;
            user-select: none;
            width: 60%;

            &.bolster,
            &.unbolster {
                border-radius: 2px;
                padding: 2% 6%;
            }

            &.top-right {
                right: -10%;
                top: -7%;
                transform: translateX(15%);
            }

            .action-icon {
                max-width: 50%;

                &.first {
                    font-size: 65%;
                    margin-right: 12%;
                    max-width: 35%;
                }

                &.last {
                    margin-left: 12%;
                }
            }
        }

		.slash-wrapper {
			position: absolute;
			width: 70%;
			height: 70%;
			display: flex;
			flex-direction: column;
			justify-content: center;
			transform: rotate(45deg);

			.action-icon-slash {
				align-self: center;
				background: white;
				border-radius: 1px;
				display: block;
				height: 8%;
				width: 100%;

				&:nth-of-type(2) {
					background: $dark-blue;
				}
			}
		}
    }

    .unit-image {
        border-radius: 50%;
        display: block;
        height: auto;
        max-height: 100%;;
        max-width: 100%;
        width: 100%;
    }

    .facedown-unit-image {
        display: none;
    }

    .poison-token {
        position: absolute;
        bottom: -7.5%;
        right: -5%;
        height: 40%;
        width: 40%;
        z-index: 10;
    }
}

.unit-stack {

    &.selected {

        .unit-coin {

            &.selected {
                border-color: $gray;

                &.archer {
                    border-color: $archer;
                }

                &.bannerman {
                    border-color: $bannerman;
                }

                &.berserker {
                    border-color: $berserker;
                }

                &.bishop {
                    border-color: $bishop;
                }

                &.cavalry {
                    border-color: $cavalry;
                }

                &.crossbowman {
                    border-color: $crossbowman;
                }

                &.ensign {
                    border-color: $ensign;
                }

                &.earl {
                    border-color: $earl;
                }

                &.footman {
                    border-color: $footman;
                }

                &.herald {
                    border-color: $herald;
                }

                &.knight {
                    border-color: $knight;
                }

                &.lancer {
                    border-color: $lancer;
                }

                &.light-cavalry {
                    border-color: $light-cavalry;
                }

                &.marshal {
                    border-color: $marshal;
                }

                &.mercenary {
                    border-color: $mercenary;
                }

                &.pikeman {
                    border-color: $pikeman;
                }

                &.pirate {
                    border-color: $pirate;
                }

                &.royal-coin-black {
                    border-color: $royal-coin-black;
                }

                &.royal-coin-white {
                    border-color: $royal-coin-white;
                }

                &.royal-guard,
                &.royal-guard-nobility {
                    border-color: $royal-guard;
                }

                &.sapper {
                    border-color: $sapper;
                }

                &.scout {
                    border-color: $scout;
                }

                &.siege-tower {
                    border-color: $siege-tower;
                }

                &.swordsman {
                    border-color: $swordsman;
                }

                &.trebuchet {
                    border-color: $trebuchet;
                }

                &.warrior-priest {
                    border-color: $warrior-priest;
                }

				&.longboat {
                    border-color: $longboat;
                }

                &.war-wagon {
                    border-color: $war-wagon;
                }
            }
        }
    }
}

.hex {

    .tile {

        &.black {

            .poison {
                background: $light-gray;

                .action-icon {
                    color: $dark-blue;
                    fill: $dark-blue;
                }
            }

            .attack {
                background: $light-gray;

                .action-icon {
                    color: $dark-blue;
                    fill: $dark-blue;
                }
            }

			.shock {
                background: $light-gray;

                .action-icon {
                    color: $dark-blue;
                    fill: $dark-blue;
                }
            }
        }

        &.fortified.is-attackable {

            .combat-actions-group {
                left: 75%;
            }


            &.mimicable-unit {

                .btn-icon {

                    &.mimic {
                        left: 75%;
                        transform: translate(-55%, -50%);
                    }
                }
            }
        }

    }

}

.mobile-player-mat {

    .unit-coin {

        &.in-supply,
        &.eliminated,
        &.in-reserve {

            .actions-wrapper {

                .btn-icon {
                    max-height: 55%;
                    width: 55%;

                    &.recruit,
                    &.resupply {
                        top: 0;
                        right: 0;
                        transform: translate(35%, -35%);
                    }
                }
            }
        }
    }
}

.play-area {

    &.submitting-action {

        .action-icon-wrapper {
            display: none;
        }
        .unit-coin {

            .actions-wrapper {
                display: none;
            }
        }
    }
}

@media only screen and (min-width: $screen-xs) {

    .unit-coin {

        .actions-wrapper {

            .btn-icon {

                &.bolster,
                &.unbolster {
                    max-height: 42%;
                    width: 42%;

                    .shield-icon {
                        width: 100%;
                    }
                }

                &.top-left {
                    right: 0;
                    top: 0;
                    transform: translate(-22%, -22%);
                }

                &.top-right {
                    right: 0;
                    top: 0;
                    transform: translate(22%, -22%);
                }
            }
        }
    }
}
</style>
